import React, { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import api from '../../utils/api';

interface Brand {
  id: number;
  name: string;
}

interface Model {
  id: number;
  modele: string;
  annee: string;
}

interface Client {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

interface AddVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (vehicleData: any) => void;
  companyId: string;
}

const AddVehicleModal: React.FC<AddVehicleModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  companyId
}) => {
  const [formData, setFormData] = useState({
    brand_id: '',
    model: '',
    conductor_password: '',
    conductor_name: '',
    client_id: '',
    plate_number: '',
    year: '',
    vin_number: '',
    kilometers: '',
    fuel_type: 'gasoline',
    transmission: 'manual'
  });

  const [brands, setBrands] = useState<Brand[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (isOpen) {
      fetchBrands();
      fetchClients();
    }
  }, [isOpen, companyId]);

  useEffect(() => {
    if (formData.brand_id) {
      fetchModels(formData.brand_id);
    } else {
      setModels([]);
      setFormData(prev => ({ ...prev, model: '', year: '' }));
    }
  }, [formData.brand_id]);

  const fetchBrands = async () => {
    setLoadingBrands(true);
    try {
      const response = await api.get('/brands');
      setBrands(response.data.data || response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des marques:', error);
      setErrors(prev => ({ ...prev, brands: 'Échec du chargement des marques' }));
    } finally {
      setLoadingBrands(false);
    }
  };

  const fetchModels = async (brandId: string) => {
    setLoadingModels(true);
    try {
      const response = await api.get(`/brands/${brandId}/models`);
      setModels(response.data.data || response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des modèles:', error);
      setErrors(prev => ({ ...prev, models: 'Échec du chargement des modèles' }));
    } finally {
      setLoadingModels(false);
    }
  };

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const response = await api.get(`/companies/${companyId}/clients`);
      setClients(response.data.data || response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
      setErrors(prev => ({ ...prev, clients: 'Échec du chargement des clients' }));
    } finally {
      setLoadingClients(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.brand_id) newErrors.brand_id = 'La marque est requise';
    if (!formData.model) newErrors.model = 'Le modèle est requis';
    if (!formData.conductor_name) newErrors.conductor_name = 'Le nom du conducteur est requis';
    if (!formData.conductor_password) newErrors.conductor_password = 'Le mot de passe est requis';
    if (formData.conductor_password.length < 8) newErrors.conductor_password = 'Le mot de passe doit contenir au moins 8 caractères';
    if (!formData.plate_number) newErrors.plate_number = 'Le numéro d\'immatriculation est requis';
    if (!formData.year) newErrors.year = 'L\'année est requise';
    if (!formData.vin_number) newErrors.vin_number = 'Le numéro VIN est requis';
    if (!formData.kilometers) newErrors.kilometers = 'Le kilométrage est requis';
    if (parseInt(formData.kilometers) < 0) newErrors.kilometers = 'Le kilométrage doit être positif';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        ...formData,
        brand_id: parseInt(formData.brand_id),
        kilometers: parseInt(formData.kilometers),
        year: formData.year,
        client_id: formData.client_id ? parseInt(formData.client_id) : null,
        company_id: parseInt(companyId)
      };

      const response = await api.post(`/companies/${companyId}/vehicles`, payload);

      if (response.status === 201 || response.status === 200) {
        await onSubmit(response.data);
        handleClose();
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout du véhicule:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ submit: 'Échec de l\'ajout du véhicule. Veuillez réessayer.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      brand_id: '',
      model: '',
      conductor_password: '',
      conductor_name: '',
      client_id: '',
      plate_number: '',
      year: '',
      vin_number: '',
      kilometers: '',
      fuel_type: 'gasoline',
      transmission: 'manual'
    });
    setErrors({});
    onClose();
  };

  const getAvailableYears = () => {
    if (!formData.model) return [];
    const selectedModelData = models.filter(m => m.modele === formData.model);
    return Array.from(new Set(selectedModelData.map(m => m.annee))).sort();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Ajouter un véhicule</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {errors.submit && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {errors.submit}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Brand */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Marque *</label>
              <select
                name="brand_id"
                value={formData.brand_id}
                onChange={handleInputChange}
                disabled={loadingBrands}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.brand_id ? 'border-red-500' : 'border-gray-300'
                } ${loadingBrands ? 'bg-gray-100' : ''}`}
              >
                <option value="">{loadingBrands ? 'Chargement des marques...' : 'Sélectionner une marque'}</option>
                {brands.map(brand => (
                  <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))}
              </select>
              {errors.brand_id && <p className="text-red-500 text-xs mt-1">{errors.brand_id}</p>}
            </div>

            {/* Model */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Modèle *</label>
              <select
                name="model"
                value={formData.model}
                onChange={handleInputChange}
                disabled={!formData.brand_id || loadingModels}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.model ? 'border-red-500' : 'border-gray-300'
                } ${(!formData.brand_id || loadingModels) ? 'bg-gray-100' : ''}`}
              >
                <option value="">{loadingModels ? 'Chargement des modèles...' : 'Sélectionner un modèle'}</option>
                {Array.from(new Set(models.map(m => m.modele))).map(modelName => (
                  <option key={modelName} value={modelName}>{modelName}</option>
                ))}
              </select>
              {errors.model && <p className="text-red-500 text-xs mt-1">{errors.model}</p>}
            </div>

            {/* Year */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Moteur *</label>
              <select
                name="year"
                value={formData.year}
                onChange={handleInputChange}
                disabled={!formData.model}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.year ? 'border-red-500' : 'border-gray-300'
                } ${!formData.model ? 'bg-gray-100' : ''}`}
              >
                <option value="">Sélectionner une année</option>
                {getAvailableYears().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              {errors.year && <p className="text-red-500 text-xs mt-1">{errors.year}</p>}
            </div>

            {/* Plate Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Numéro d'immatriculation *</label>
              <input
                type="text"
                name="plate_number"
                value={formData.plate_number}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.plate_number ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="ex: ABC-1234"
              />
              {errors.plate_number && <p className="text-red-500 text-xs mt-1">{errors.plate_number}</p>}
            </div>

            {/* VIN */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Numéro VIN *</label>
              <input
                type="text"
                name="vin_number"
                value={formData.vin_number}
                onChange={handleInputChange}
                maxLength={17}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.vin_number ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Numéro VIN (17 caractères)"
              />
              {errors.vin_number && <p className="text-red-500 text-xs mt-1">{errors.vin_number}</p>}
            </div>

            {/* Kilometers */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kilométrage *</label>
              <input
                type="number"
                name="kilometers"
                value={formData.kilometers}
                onChange={handleInputChange}
                min="0"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.kilometers ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="ex: 50000"
              />
              {errors.kilometers && <p className="text-red-500 text-xs mt-1">{errors.kilometers}</p>}
            </div>

            {/* Fuel Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type de carburant *</label>
              <select
                name="fuel_type"
                value={formData.fuel_type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="gasoline">Essence</option>
                <option value="diesel">Diesel</option>
                <option value="electric">Électrique</option>
                <option value="hybrid">Hybride</option>
              </select>
            </div>

            {/* Transmission */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Transmission *</label>
              <select
                name="transmission"
                value={formData.transmission}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="manual">Manuelle</option>
                <option value="automatic">Automatique</option>
              </select>
            </div>

            {/* Conductor Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom du conducteur *</label>
              <input
                type="text"
                name="conductor_name"
                value={formData.conductor_name}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.conductor_name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Jean Dupont"
              />
              {errors.conductor_name && <p className="text-red-500 text-xs mt-1">{errors.conductor_name}</p>}
            </div>

            {/* Responsable */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Responsable (optionnel)</label>
              <select
                name="client_id"
                value={formData.client_id}
                onChange={handleInputChange}
                disabled={loadingClients}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  loadingClients ? 'bg-gray-100' : ''
                }`}
              >
                <option value="">{loadingClients ? 'Chargement des clients...' : 'Sélectionner un responsable'}</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.first_name} {client.last_name}</option>
                ))}
              </select>
            </div>

            {/* Password */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe du conducteur *</label>
              <input
                type="password"
                name="conductor_password"
                value={formData.conductor_password}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.conductor_password ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Entrer un mot de passe (min. 8 caractères)"
              />
              {errors.conductor_password && <p className="text-red-500 text-xs mt-1">{errors.conductor_password}</p>}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Ajout...
                </>
              ) : (
                <>
                  <Plus size={16} />
                  Ajouter le véhicule
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddVehicleModal;
